/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {Component, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {BusinessSettingsModal} from '../business-settings-modal';
import {BusinessSettingsModalState} from '../business-settings-modal.state';
import {InputMessagesService} from '../../../../shared/input/service/input-messages.service';
import {InputListModel} from '../input-list.model';
import {EntityElementModel} from '../../../../shared/entity-tree/entity-element/models/entity-element.model';
import {TypeaheadHelperService} from '../../../../shared/services/typeahead-helper.service';
import {ValidatorFn, Validators} from '@angular/forms';
import {timer} from 'rxjs';
import {take} from 'rxjs/operators';
import {MandatoryMessageTypeEnum} from '../../../../shared/input/service/mandatory-message-type.enum';
import {WarningsEnum} from '../../../../shared/warnings/warnings.enum';
import {InputTextComponent} from '../../../../shared/input/input-text/input-text.component';
import {InputTypeEnum} from '../../../../shared/input/input-type.enum';
import {DecisionCodeModel} from '../../../../api/ms-settings/business/schema/v1/contracts/pool/decision-code.model';
import {TooltipEnum} from '../../../../shared/tooltip/tooltip.enum';
import {InputMultiSelectOptionModel} from '../../../../shared/input/input-multi-select/input-multi-select-option.model';

@Component({
    selector: 'app-decision-code-modal',
    templateUrl: './decision-code-modal.component.html',
    styleUrls: ['../business-settings-modal.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DecisionCodeModalComponent extends BusinessSettingsModal<BusinessSettingsModalState<DecisionCodeModel>> {

    @ViewChild('decisionCodeName') nameInput: InputTextComponent;
    @ViewChild('decisionCode') codeInput: InputTextComponent;
    @ViewChild('selfExclusion') selfExclusionInput: InputTextComponent;

    readonly ENTITY_NAME = 'decision code';
    readonly EDIT_TITLE = 'Edit decision code';
    readonly ADD_TITLE = 'Add a decision code';
    readonly EDIT_SUBMIT_TITLE = 'Edit';
    readonly ADD_SUBMIT_TITLE = 'Add';

    nameMessages = new Map<WarningsEnum, string[]>();
    codeMessages = new Map<WarningsEnum, string[]>();

    validators: { [key: string]: ValidatorFn[] } = {
        name: [Validators.pattern(/^[\w-/\s\\.(),]{1,100}$/)],
        code: [Validators.pattern(/^\d{2}$/)]
    };

    tooltipTypeEnum = TooltipEnum;

    selfExclusionOptions: InputMultiSelectOptionModel<DecisionCodeModel>[] = [];

    constructor(public typeahead: TypeaheadHelperService,
                inputMessagesService: InputMessagesService) {
        super(inputMessagesService);
        this.storeState({
            currentValue: {
                ...this.state.currentValue,
                hasTimeEffort: false
            }
        });
    }

    getInitialData(): void {
        super.getInitialData();
        this.mapSelfExclusionOptions();
    }

    setEntity(element: EntityElementModel<DecisionCodeModel>): void {
        this.storeState({
            originalElement: element,
            originalValue: element.originalValue,
            currentValue: element.currentValue
        });

        this.onReady.pipe(take(1))
.subscribe(() => {
            timer(0)
.subscribe(() => {
                this.mapSelfExclusionOptions();

                this.nameInput.setValue(this.state.currentValue.name);
                this.codeInput.setValue(this.state.currentValue.code);
                if (this.state.currentValue.excludedDecisionCodes != null) {
                    this.selfExclusionInput.setValue(this.selectedSelfExclusionOptions());
                }

                this.hasTimeEffortInputChange(this.state.currentValue.hasTimeEffort, true);
                this.setActiveInputState(element.active, true);
            });
        });
    }

    nameInputChange(name: string): void {
        this.inputMessages.mandatoryWarnings(this.nameInput.inputControl, this.nameMessages, MandatoryMessageTypeEnum.SAVE);
        this.inputMessages.invalidInputValueError(this.nameInput.inputControl, this.nameMessages, this.nameInput.label);
        this.nameMessages = new Map(this.nameMessages);
        const errors = this.nameMessages.get(WarningsEnum.ERROR);

        this.storeState({
            currentValue: {
                ...this.state.currentValue,
                name: name
            },
            invalid: {
                ...this.state.invalid,
                name: (name == null || name.length === 0) || (errors != null && errors.length > 0)
            }
        });
        this.setModalValidity();
    }

    codeInputChange(code: string): void {
        this.inputMessages.mandatoryWarnings(this.codeInput.inputControl, this.codeMessages, MandatoryMessageTypeEnum.SAVE);
        this.inputMessages.invalidInputValueError(
                this.codeInput.inputControl,
                this.codeMessages,
                this.codeInput.label,
                '00'
        );

        this.codeMessages = new Map(this.codeMessages);

        const errors = this.codeMessages.get(WarningsEnum.ERROR);

        if (errors.length === 0) {
            this.manageAlreadyExistsCode(code, 'decision', this.codeMessages, this.codeInput);
        }

        this.storeState({
            currentValue: {
                ...this.state.currentValue,
                code: code
            },
            invalid: {
                ...this.state.invalid,
                code: (code == null || code.length === 0) || (errors != null && errors.length > 0)
            }
        });
        this.setModalValidity();
    }

    selfExclusionInputChange(excludedCodes: InputMultiSelectOptionModel<DecisionCodeModel>[]): void {
        let selfExcludedCodes: string[] = [];
        if (excludedCodes != null) {
            selfExcludedCodes = excludedCodes.map((item) => item.value.code);
        }
        this.storeState({
            currentValue: {
                ...this.state.currentValue,
                excludedDecisionCodes: selfExcludedCodes
            }
        });
        this.setModalValidity();
    }

    hasTimeEffortInputChange(hasTimeEffort: boolean, isSetInitialValue?: boolean): void {
        this.storeState({
            currentValue: {
                ...this.state.currentValue,
                hasTimeEffort: hasTimeEffort
            }
        });
        this.setModalValidity(isSetInitialValue);
    }

    protected generateInputs(): InputListModel[] {
        return [{
            input: this.nameInput,
            type: InputTypeEnum.TEXT,
            mandatory: true
        }, {
            input: this.codeInput,
            type: InputTypeEnum.TEXT,
            mandatory: true
        }, {
            input: this.selfExclusionInput,
            type: InputTypeEnum.MULTI_SELECT,
            mandatory: false
        }];
    }

    private selectedSelfExclusionOptions(): InputMultiSelectOptionModel<DecisionCodeModel>[] {
        return this.selfExclusionOptions.filter((option) =>
                this.state.currentValue.excludedDecisionCodes.find((item) => item === option.value.code) != null);

    }

    private mapSelfExclusionOptions(): void {
        this.selfExclusionOptions = this.excludedEntities
                .filter((option) => option.currentValue.code !== this.state.currentValue.code)
                .map((option) => ({
                    value: option.currentValue,
                    active: true,
                    label: `${option.currentValue.name} ${option.currentValue.code}`
                }));
    }
}
